import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth';
import { TechNoteEntry } from '../types';
import { aliasFromEmail } from './eventModal/saveHelpers';

const TECH_NOTE_MAX_LENGTH = 4000;

interface TechNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  entries: readonly TechNoteEntry[];
  onEntriesChange: (entries: TechNoteEntry[]) => void;
}

interface TechNoteTimestamp {
  date: string;
  time: string;
}

function getZuluTimestamp(selectedAt: Date): TechNoteTimestamp {
  return {
    date: selectedAt.toISOString().slice(0, 10),
    time: selectedAt.toISOString().slice(11, 16),
  };
}

interface TechNoteHistoryProps {
  entries: readonly TechNoteEntry[];
}

/**
 * Renders every Tech Note stored against the event — one row per note in the
 * tech-notes table (`event_id` + `tech_note_id`) — showing each note's id
 * alongside the Zulu timestamp, who logged it, and the text.
 */
const TechNoteHistory: React.FC<TechNoteHistoryProps> = ({ entries }) => {
  if (entries.length === 0) return null;
  return (
    <div className="field-row field-row-tall">
      <label className="field-label">Tech Note History ({entries.length})</label>
      <div className="field-group defect-group">
        {entries.map((entry: TechNoteEntry) => (
          <div key={entry.id} className="note-entry">
            <div className="note-entry-header">
              <span className="note-timestamp">
                {entry.date} {entry.time}z
              </span>
              <span className="note-alias">({entry.alias})</span>
              <span className="tech-note-id" title="Tech Note ID">
                {entry.id}
              </span>
            </div>
            <div className="note-entry-text">{entry.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Displays append-only event Tech Notes and captures the selection timestamp. */
const TechNotesModal: React.FC<TechNotesModalProps> = ({
  isOpen,
  onClose,
  entries,
  onEntriesChange,
}) => {
  const { user } = useAuth();
  const [timestamp, setTimestamp] = useState<TechNoteTimestamp>({ date: '', time: '' });
  const [noteText, setNoteText] = useState<string>('');
  const userAlias: string = aliasFromEmail(user?.email) || '—';
  const sortedEntries: TechNoteEntry[] = useMemo(
    (): TechNoteEntry[] =>
      [...entries].sort((left: TechNoteEntry, right: TechNoteEntry): number => {
        const timestampComparison: number = `${right.date} ${right.time}`.localeCompare(
          `${left.date} ${left.time}`,
        );
        return timestampComparison || right.id.localeCompare(left.id);
      }),
    [entries],
  );

  useEffect((): void => {
    if (!isOpen) return;
    setTimestamp(getZuluTimestamp(new Date()));
    setNoteText('');
  }, [isOpen]);

  const handleAccept = (): void => {
    const text: string = noteText.trim();
    if (!text) return;
    const entry: TechNoteEntry = {
      id: `tech-note-${Date.now()}`,
      date: timestamp.date,
      time: timestamp.time,
      alias: userAlias,
      text,
    };
    onEntriesChange([...entries, entry]);
    setNoteText('');
  };

  if (!isOpen) return null;
  return (
    <div className="modal-overlay active" role="dialog" aria-modal="true" aria-label="Tech Notes">
      <div
        className="modal audit-modal"
        onClick={(event: React.MouseEvent<HTMLDivElement>): void => event.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Tech Notes</h2>
          <button className="modal-close" onClick={onClose} type="button" aria-label="Close">
            &times;
          </button>
        </div>
        <div className="modal-body-scroll">
          <div className="field-row">
            <label className="field-label">Date &amp; Time</label>
            <div className="field-group">
              <div className="field-input-block">
                <span className="field-input-label">Date</span>
                <input className="field-input" type="date" value={timestamp.date} readOnly />
              </div>
              <div className="field-input-block">
                <span className="field-input-label">Time (Z)</span>
                <input className="field-input" type="time" value={timestamp.time} readOnly />
              </div>
            </div>
          </div>
          <div className="field-row">
            <label className="field-label">Logged By</label>
            <div className="field-group">
              <input className="field-input" type="text" value={userAlias} readOnly />
            </div>
          </div>
          <div className="field-row field-row-tall">
            <label className="field-label">Tech Note</label>
            <div className="field-group defect-group">
              <textarea
                className="field-textarea"
                value={noteText}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
                  setNoteText(event.target.value)
                }
                rows={4}
                maxLength={TECH_NOTE_MAX_LENGTH}
                placeholder="Enter tech note..."
              />
              <div className="audit-note-actions">
                <button
                  className="modal-btn modal-btn-save audit-note-submit-btn"
                  type="button"
                  disabled={!noteText.trim()}
                  onClick={handleAccept}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
          <TechNoteHistory entries={sortedEntries} />
        </div>
        <div className="modal-footer">
          <button className="modal-btn modal-btn-cancel" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TechNotesModal;
